import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { useAuthorization } from '@/modules/auth/useAuthorization';
import { Forbidden } from '@/modules/auth/Forbidden';
import { NotFound } from '@/shared/ui/NotFound';
import { apiErrorMessage } from '@/shared/api-client';
import { Button } from '@/shared/ui/components/button';
import { Checkbox } from '@/shared/ui/components/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/ui/components/card';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { Textarea } from '@/shared/ui/components/textarea';
import {
  useCreateWaiverTemplate,
  useCreateWaiverTemplateVersion,
  useVenues,
  useWaiverTemplates,
  type Venue,
  type WaiverTemplate,
} from './venues';

type AcknowledgementDraft = {
  text: string;
  required: boolean;
};

type WaiverEditorMode = 'create' | 'version';

type Translate = ReturnType<typeof useTranslation>['t'];

const adminWaiversPath = '/admin/waivers';
const newAcknowledgement = (): AcknowledgementDraft => ({ text: '', required: true });

export function AdminWaiversPage() {
  const { t } = useTranslation('common');
  const auth = useAuthorization();
  const canManage = auth.can('venue.waiver_template.manage');

  const { data: waiverTemplates, isLoading, isError } = useWaiverTemplates();
  const { data: venuesData } = useVenues();
  const [selectedId, setSelectedId] = useState<string>('');
  const templates = waiverTemplates?.items;
  const templateList = templates ?? [];
  const venues = venuesData?.items ?? [];
  const counts = countAssignmentsByTemplate(venues);
  const selected = selectedTemplate(templateList, selectedId);

  useEffect(() => {
    if (!templates || templates.length === 0) {
      setSelectedId('');
      return;
    }
    if (!selectedId || !templates.some((template) => template.id === selectedId)) {
      setSelectedId(templates[0]!.id);
    }
  }, [selectedId, templates]);

  if (!canManage) return <Forbidden />;

  return (
    <section className="grid gap-6 p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="grid gap-1">
          <h1 className="text-2xl font-semibold">{t('venues.waivers.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('venues.waivers.description')}</p>
        </div>
        <Button asChild>
          <Link to={`${adminWaiversPath}/new`}>
            <Plus aria-hidden="true" />
            <span>{t('venues.waivers.actions.create')}</span>
          </Link>
        </Button>
      </div>

      {isLoading && <p className="text-muted-foreground">{t('venues.waivers.loading')}</p>}
      {isError && <p className="text-destructive">{t('venues.waivers.errors.loadFailed')}</p>}

      {waiverTemplates && templateList.length === 0 && (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            {t('venues.waivers.empty')}
          </CardContent>
        </Card>
      )}

      {templateList.length > 0 && (
        <div className="grid gap-4 rounded-xl border bg-card md:grid-cols-[280px_1fr]">
          <div className="divide-y border-b md:border-r md:border-b-0">
            {templateList.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => setSelectedId(template.id)}
                className={`flex w-full flex-col gap-1 px-4 py-3 text-left hover:bg-muted/50 ${
                  template.id === selected?.id ? 'bg-muted' : ''
                }`}
              >
                <span className="font-medium">{template.name}</span>
                <span className="text-xs text-muted-foreground">
                  v{template.latestVersion.version} · {counts.get(template.id) ?? 0} venues
                </span>
              </button>
            ))}
          </div>

          {selected && (
            <div data-testid="waiver-detail" className="grid gap-4 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="grid gap-1">
                  <h2 className="text-lg font-semibold">{selected.name}</h2>
                  <p className="text-sm text-muted-foreground">
                    Created {new Date(selected.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <Button size="sm" variant="outline" asChild>
                  <Link to={newVersionPath(selected.id)}>
                    {t('venues.waivers.actions.newVersion')}
                  </Link>
                </Button>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{t('venues.waivers.detail.latestVersion')}</CardTitle>
                  <CardDescription>
                    {t('venues.waivers.detail.assignments', {
                      count: counts.get(selected.id) ?? 0,
                    })}
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 text-sm">
                  <div className="grid gap-2">
                    <p className="font-medium">v{selected.latestVersion.version}</p>
                    <div
                      className="prose prose-sm max-w-none text-foreground"
                      dangerouslySetInnerHTML={{ __html: selected.latestVersion.bodyHtml }}
                    />
                  </div>

                  <div className="grid gap-2">
                    <p className="font-medium">{t('venues.waivers.fields.acknowledgements')}</p>
                    {selected.latestVersion.acknowledgements.length === 0 ? (
                      <p className="text-muted-foreground">
                        {t('venues.waivers.noAcknowledgements')}
                      </p>
                    ) : (
                      <AcknowledgementList
                        acknowledgements={selected.latestVersion.acknowledgements}
                      />
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t('venues.waivers.detail.history')}</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-2">
                  {selected.versions
                    .slice()
                    .sort((left, right) => right.version - left.version)
                    .map((version) => (
                      <div
                        key={version.version}
                        className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                      >
                        <span>v{version.version}</span>
                        <span className="text-muted-foreground">
                          {formatDate(version.createdAt)}
                        </span>
                      </div>
                    ))}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function AdminWaiverTemplateFormPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const auth = useAuthorization();
  const { templateId } = useParams();
  const canManage = auth.can('venue.waiver_template.manage');

  const createTemplate = useCreateWaiverTemplate();
  const createVersion = useCreateWaiverTemplateVersion();
  const { data: waiverTemplates, isLoading, isError } = useWaiverTemplates();

  const template = waiverTemplates?.items.find((item) => item.id === templateId) ?? null;
  const mode: WaiverEditorMode = templateId ? 'version' : 'create';

  const [name, setName] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [acknowledgements, setAcknowledgements] = useState<AcknowledgementDraft[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'create') {
      resetEditor(setName, setBodyHtml, setAcknowledgements, setError);
      return;
    }
    if (!template) return;
    populateEditorFromTemplate(template, setName, setBodyHtml, setAcknowledgements, setError);
  }, [mode, template]);

  if (!canManage) return <Forbidden />;
  if (mode === 'version' && isLoading) {
    return (
      <section className="grid gap-6 p-6 text-muted-foreground">
        {t('venues.waivers.loading')}
      </section>
    );
  }
  if (mode === 'version' && isError) {
    return (
      <section className="grid gap-6 p-6 text-destructive">
        {t('venues.waivers.errors.loadFailed')}
      </section>
    );
  }
  if (mode === 'version' && !template) return <NotFound />;

  function updateAcknowledgement(index: number, patch: Partial<AcknowledgementDraft>) {
    setAcknowledgements((current) =>
      current.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
    );
  }

  function addAcknowledgement() {
    setAcknowledgements((current) => [...current, newAcknowledgement()]);
  }

  function removeAcknowledgement(index: number) {
    setAcknowledgements((current) => current.filter((_, itemIndex) => itemIndex !== index));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    try {
      const input = templateEditorInput(bodyHtml, acknowledgements);
      switch (mode) {
        case 'create':
          await createTemplate.mutateAsync({ name, ...input });
          toast.success(t('venues.waivers.toast.created'));
          navigate(adminWaiversPath);
          return;
        case 'version':
          if (!template) return;
          await createVersion.mutateAsync({ templateId: template.id, input });
          toast.success(t('venues.waivers.toast.versionCreated'));
          navigate(adminWaiversPath);
          return;
      }
    } catch (caught) {
      setError(apiErrorMessage(t, 'venues', caught));
    }
  }

  const formCopy = waiverTemplateFormCopy(
    mode,
    template?.name ?? '',
    createTemplate.isPending,
    createVersion.isPending,
    t,
  );

  return (
    <section className="mx-auto grid max-w-3xl gap-6 p-6">
      <div className="grid gap-2">
        <button
          type="button"
          onClick={() => navigate(adminWaiversPath)}
          className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          {t('venues.waivers.actions.back')}
        </button>
        <div className="grid gap-1">
          <h1 className="text-2xl font-semibold">{formCopy.title}</h1>
          <p className="text-sm text-muted-foreground">{formCopy.description}</p>
        </div>
      </div>

      <form onSubmit={submit} className="grid gap-5">
        <Card>
          <CardContent className="grid gap-4 pt-6">
            {mode === 'create' && (
              <div className="grid gap-2">
                <Label htmlFor="waiver-name">{t('venues.waivers.fields.name')}</Label>
                <Input
                  id="waiver-name"
                  required
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="waiver-body">{t('venues.waivers.fields.body')}</Label>
              <Textarea
                id="waiver-body"
                required
                value={bodyHtml}
                onChange={(event) => setBodyHtml(event.target.value)}
                rows={10}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('venues.waivers.fields.acknowledgements')}</CardTitle>
            <CardDescription>{t('venues.waivers.acknowledgementsHint')}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            <div className="flex justify-end">
              <Button type="button" variant="outline" size="sm" onClick={addAcknowledgement}>
                {t('venues.waivers.actions.addAcknowledgement')}
              </Button>
            </div>

            {acknowledgements.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t('venues.waivers.noAcknowledgements')}
              </p>
            )}

            {acknowledgements.map((item, index) => (
              <div key={index} className="grid gap-3 rounded-md border p-3">
                <div className="grid gap-2">
                  <Label htmlFor={`waiver-ack-${index}`}>
                    {t('venues.waivers.fields.ackText')}
                  </Label>
                  <Input
                    id={`waiver-ack-${index}`}
                    value={item.text}
                    onChange={(event) => updateAcknowledgement(index, { text: event.target.value })}
                  />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={item.required}
                    onCheckedChange={(checked) =>
                      updateAcknowledgement(index, { required: checked !== false })
                    }
                  />
                  {t('venues.waivers.fields.required')}
                </label>
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeAcknowledgement(index)}
                  >
                    {t('venues.waivers.actions.removeAcknowledgement')}
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(adminWaiversPath)}>
            {t('actions.cancel')}
          </Button>
          <Button type="submit" disabled={createTemplate.isPending || createVersion.isPending}>
            {formCopy.submitLabel}
          </Button>
        </div>
      </form>
    </section>
  );
}

function AcknowledgementList({ acknowledgements }: { acknowledgements: AcknowledgementDraft[] }) {
  return (
    <ul className="grid gap-1 text-muted-foreground">
      {acknowledgements.map((acknowledgement, index) => (
        <li key={`${acknowledgement.text}-${index}`}>
          {acknowledgement.required ? '● required' : '○ optional'} {acknowledgement.text}
        </li>
      ))}
    </ul>
  );
}

function countAssignmentsByTemplate(venues: Venue[]) {
  const counts = new Map<string, number>();
  for (const venue of venues) {
    const templateId = venue.activeWaiverTemplate?.templateId;
    if (!templateId) continue;
    counts.set(templateId, (counts.get(templateId) ?? 0) + 1);
  }
  return counts;
}

function templateEditorInput(bodyHtml: string, acknowledgements: AcknowledgementDraft[]) {
  return {
    bodyHtml,
    acknowledgements: acknowledgements.filter((item) => item.text.trim().length > 0),
  };
}

function selectedTemplate(templates: WaiverTemplate[], selectedId: string) {
  return templates.find((template) => template.id === selectedId) ?? templates[0] ?? null;
}

function newVersionPath(templateId: string) {
  return `${adminWaiversPath}/${templateId}/versions/new`;
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString();
}

function resetEditor(
  setName: (value: string) => void,
  setBodyHtml: (value: string) => void,
  setAcknowledgements: (value: AcknowledgementDraft[]) => void,
  setError: (value: string | null) => void,
) {
  setName('');
  setBodyHtml('');
  setAcknowledgements([]);
  setError(null);
}

function populateEditorFromTemplate(
  template: WaiverTemplate,
  setName: (value: string) => void,
  setBodyHtml: (value: string) => void,
  setAcknowledgements: (value: AcknowledgementDraft[]) => void,
  setError: (value: string | null) => void,
) {
  setName(template.name);
  setBodyHtml(template.latestVersion.bodyHtml);
  setAcknowledgements(template.latestVersion.acknowledgements);
  setError(null);
}

function waiverTemplateFormCopy(
  mode: WaiverEditorMode,
  templateName: string,
  createPending: boolean,
  versionPending: boolean,
  t: Translate,
) {
  if (mode === 'create') {
    return {
      title: t('venues.waivers.create.title'),
      description: t('venues.waivers.create.description'),
      submitLabel: createPending
        ? t('venues.waivers.create.submitting')
        : t('venues.waivers.create.submit'),
    };
  }

  return {
    title: t('venues.waivers.version.title', { name: templateName }),
    description: t('venues.waivers.version.description'),
    submitLabel: versionPending
      ? t('venues.waivers.version.submitting')
      : t('venues.waivers.version.submit'),
  };
}
